/**
 * @file IssueManagementPanel.tsx
 * @description Panel for managing issue details, assignments, notes, and history
 */

import React, { useState, useEffect } from "react";
import { useRulesStore } from "@/store/rulesStore";
import type {
  ValidationResult,
  IssuePriority,
  IssueStatus,
} from "@/types/rules";

// Determine email service URL based on environment
const getEmailServiceUrl = () => {
  // Use environment variable if set
  if (import.meta.env.VITE_EMAIL_SERVICE_URL) {
    return import.meta.env.VITE_EMAIL_SERVICE_URL;
  }

  // Check if we're in production
  if (
    typeof window !== "undefined" &&
    window.location.hostname !== "localhost" &&
    window.location.hostname !== "127.0.0.1"
  ) {
    return "https://email-service-erlz.onrender.com";
  }

  // Development fallback
  return "http://localhost:3001";
};

interface IssueManagementPanelProps {
  issue: ValidationResult;
  onClose: () => void;
}

export function IssueManagementPanel({
  issue: originalIssue,
  onClose,
}: IssueManagementPanelProps) {
  const { availableUsers, loadUsers } = useRulesStore();

  // Local state for the issue being edited
  const [localIssue, setLocalIssue] = useState<ValidationResult>(originalIssue);
  const [noteText, setNoteText] = useState("");
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [showEmailPopup, setShowEmailPopup] = useState(false);
  const [emailAddress, setEmailAddress] = useState("");
  const [emailValidationError, setEmailValidationError] = useState<
    string | null
  >(null);

  // Load users on mount
  useEffect(() => {
    if (availableUsers.length === 0) {
      loadUsers();
    }
  }, [availableUsers.length, loadUsers]);

  // Simple save function that updates the store directly
  const handleSave = () => {
    const store = useRulesStore.getState();

    // Generate issue ID if it doesn't exist
    const issueId =
      localIssue.issueId ||
      `issue-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Update the validation results in the store
    store.validationResults.forEach((result, index) => {
      if (
        result.elementId === originalIssue.elementId &&
        result.ruleId === originalIssue.ruleId
      ) {
        store.validationResults[index] = {
          ...result,
          ...localIssue,
          issueId,
        };
      }
    });

    // Force a state update
    useRulesStore.setState({
      validationResults: [...store.validationResults],
    });

    onClose();
  };

  const handleAssignUser = (userId: string) => {
    const user = availableUsers.find((u) => u.id === userId);
    if (!user) return;

    const currentUser = useRulesStore.getState().currentUser;

    setLocalIssue({
      ...localIssue,
      assignment: {
        assignedTo: user,
        assignedBy: currentUser || {
          id: "current",
          name: "Current User",
          email: "",
        },
        assignedAt: new Date(),
      },
      issueStatus: "in_progress",
    });
  };

  const handleUnassign = () => {
    setLocalIssue({
      ...localIssue,
      assignment: undefined,
      issueStatus: "open",
    });
  };

  const handlePriorityChange = (priority: IssuePriority) => {
    console.log("Priority changed to:", priority);
    setLocalIssue({
      ...localIssue,
      issuePriority: priority,
    });
  };

  const handleStatusChange = (status: IssueStatus) => {
    setLocalIssue({
      ...localIssue,
      issueStatus: status,
    });
  };

  const handleDueDateChange = (dateString: string) => {
    setLocalIssue({
      ...localIssue,
      dueDate: dateString ? new Date(dateString) : undefined,
    });
  };

  const handleAddNote = () => {
    if (!noteText.trim()) return;

    const currentUser = useRulesStore.getState().currentUser;
    const newNote = {
      id: `note-${Date.now()}`,
      userId: currentUser?.id || "current",
      userName: currentUser?.name || "Current User",
      text: noteText,
      timestamp: new Date(),
    };

    setLocalIssue({
      ...localIssue,
      notes: [...(localIssue.notes || []), newNote],
    });

    setNoteText("");
  };

  // Validate email format
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Open email popup
  const handleOpenEmailPopup = () => {
    // Pre-fill with assignee email if available
    const assigneeEmail = localIssue.assignment?.assignedTo?.email || "";
    setEmailAddress(assigneeEmail);
    setEmailValidationError(null);
    setShowEmailPopup(true);
  };

  // Close email popup
  const handleCloseEmailPopup = () => {
    setShowEmailPopup(false);
    setEmailAddress("");
    setEmailValidationError(null);
    setEmailError(null);
  };

  // Handle sending email from popup
  const handleConfirmSendEmail = async () => {
    // Validate email
    if (!emailAddress.trim()) {
      setEmailValidationError("Email address is required");
      return;
    }

    if (!validateEmail(emailAddress.trim())) {
      setEmailValidationError("Please enter a valid email address");
      return;
    }

    setEmailValidationError(null);
    await handleSendEmail(emailAddress.trim());
  };

  const handleSendEmail = async (recipientEmail: string) => {
    setIsSendingEmail(true);
    setEmailError(null);

    try {
      // Get assignee name for greeting
      const assigneeName = localIssue.assignment?.assignedTo?.name || "Team";

      // Get issue title
      const issueTitle = localIssue.ruleName || "Issue";

      // Format status for display
      const formatStatus = (status?: string) => {
        if (!status) return "Open";
        if (status === "in_progress") return "In Progress";
        return status.charAt(0).toUpperCase() + status.slice(1);
      };

      // Format validation status for display
      const formatValidationStatus = (status?: string) => {
        if (!status) return "Unknown";
        return status.charAt(0).toUpperCase() + status.slice(1);
      };

      // Build email body
      const emailBody = `Hello ${assigneeName}

Detected Issue - ${issueTitle}

${localIssue.elementName}
${localIssue.message}
${localIssue.standard ? `Standard: ${localIssue.standard}` : ""}

Validation Status: ${formatValidationStatus(localIssue.status)}

Priority: ${localIssue.issuePriority || "Not set"}

Status: ${formatStatus(localIssue.issueStatus)}

Due date: ${localIssue.dueDate ? new Date(localIssue.dueDate).toLocaleDateString() : "Not set"}

Notes:
${
  localIssue.notes && localIssue.notes.length > 0
    ? localIssue.notes
        .map(
          (n) =>
            `- ${n.userName} (${new Date(n.timestamp).toLocaleString()}): ${n.text}`
        )
        .join("\n")
    : "No notes"
}`;

      // Create subject with issue title
      const subject = `HeroCon Issue - ${issueTitle} assigned to you`;

      // Send email via email service
      const emailServiceUrl = getEmailServiceUrl();
      const response = await fetch(`${emailServiceUrl}/api/send-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          to: [recipientEmail],
          subject: subject,
          text: emailBody,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setShowEmailPopup(false);
        setEmailAddress("");
      } else {
        throw new Error(result.error || "Failed to send email");
      }
    } catch (error) {
      setEmailError(
        error instanceof Error ? error.message : "Failed to send email"
      );
    } finally {
      setIsSendingEmail(false);
    }
  };

  const getPriorityColor = (priority?: IssuePriority) => {
    switch (priority) {
      case "critical":
        return "bg-red-500 text-white border-red-600";
      case "high":
        return "bg-orange-500 text-white border-orange-600";
      case "medium":
        return "bg-yellow-500 text-white border-yellow-600";
      case "low":
        return "bg-green-500 text-white border-green-600";
      default:
        return "bg-gray-200 text-gray-700 border-gray-300";
    }
  };

  const getStatusColor = (status?: IssueStatus) => {
    switch (status) {
      case "open":
        return "bg-blue-100 text-blue-800";
      case "in_progress":
        return "bg-yellow-100 text-yellow-800";
      case "resolved":
        return "bg-green-100 text-green-800";
      case "closed":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 bg-custom-navy border-b border-custom-dark flex items-center justify-between">
          <h2 className="text-xl font-semibold text-white">Manage Issue</h2>
          <button
            onClick={onClose}
            className="text-white hover:text-custom-light-100 transition-colors"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {/* Email Error Message */}
          {emailError && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-600">{emailError}</p>
            </div>
          )}

          {/* Issue Details */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">
              Issue Details
            </h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="font-medium text-gray-900">
                    {localIssue.ruleName}
                  </div>
                  <div className="text-sm text-gray-600">
                    {localIssue.elementName}
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    {localIssue.message}
                  </div>
                </div>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(localIssue.issueStatus)}`}
                >
                  {localIssue.issueStatus || "open"}
                </span>
              </div>
              {localIssue.standard && (
                <div className="text-xs text-gray-500">
                  Standard:{" "}
                  <span className="font-medium">{localIssue.standard}</span>
                </div>
              )}
            </div>
          </div>

          {/* Assignment */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">
              Assign To
            </h3>
            <select
              value={localIssue.assignment?.assignedTo?.id || ""}
              onChange={(e) => {
                if (e.target.value) {
                  handleAssignUser(e.target.value);
                } else {
                  handleUnassign();
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">-- Unassigned --</option>
              {availableUsers.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name} ({user.role || "team member"})
                </option>
              ))}
            </select>

            {localIssue.assignment?.assignedTo && (
              <div className="mt-3 p-3 bg-blue-50 rounded-md">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="w-10 h-10 rounded-full bg-custom-navy text-white flex items-center justify-center font-medium">
                      {localIssue.assignment.assignedTo.name
                        .split(" ")
                        .map((n) => n[0])
                        .join("")
                        .toUpperCase()
                        .slice(0, 2)}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">
                        {localIssue.assignment.assignedTo.name}
                      </div>
                      <div className="text-sm text-gray-600">
                        {localIssue.assignment.assignedTo.email}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={handleOpenEmailPopup}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm font-medium shadow-sm flex items-center gap-2 whitespace-nowrap transition-colors"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                    Email Issue
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Priority */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">Priority</h3>
            <div className="grid grid-cols-4 gap-2">
              {(["low", "medium", "high", "critical"] as IssuePriority[]).map(
                (priority) => (
                  <button
                    key={priority}
                    onClick={() => handlePriorityChange(priority)}
                    className={`px-4 py-2 rounded-md text-sm font-medium border-2 transition-all ${
                      localIssue.issuePriority === priority
                        ? getPriorityColor(priority)
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {priority.charAt(0).toUpperCase() + priority.slice(1)}
                  </button>
                )
              )}
            </div>
          </div>

          {/* Status */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">Status</h3>
            <select
              value={localIssue.issueStatus || "open"}
              onChange={(e) =>
                handleStatusChange(e.target.value as IssueStatus)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </div>

          {/* Due Date */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">Due Date</h3>
            <input
              type="date"
              value={
                localIssue.dueDate
                  ? new Date(localIssue.dueDate).toISOString().split("T")[0]
                  : ""
              }
              onChange={(e) => handleDueDateChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Notes */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">Notes</h3>

            {/* Add Note */}
            <div className="mb-3">
              <textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Add a note..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                onClick={handleAddNote}
                disabled={!noteText.trim()}
                className="mt-2 px-4 py-2 bg-custom-navy text-white rounded-md hover:bg-custom-dark disabled:bg-gray-300 disabled:cursor-not-allowed text-sm"
              >
                Add Note
              </button>
            </div>

            {/* Notes List */}
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {localIssue.notes && localIssue.notes.length > 0 ? (
                localIssue.notes.map((note) => (
                  <div
                    key={note.id}
                    className="bg-gray-50 rounded-lg p-3 border border-gray-200"
                  >
                    <div className="flex items-start justify-between mb-1">
                      <div className="font-medium text-sm text-gray-900">
                        {note.userName}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(note.timestamp).toLocaleString()}
                      </div>
                    </div>
                    <div className="text-sm text-gray-700">{note.text}</div>
                  </div>
                ))
              ) : (
                <div className="text-sm text-gray-500 italic py-2">
                  No notes yet
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-2 bg-custom-navy text-white rounded-md hover:bg-custom-dark font-medium shadow-sm"
          >
            Save Changes
          </button>
        </div>
      </div>

      {/* Email Popup Modal */}
      {showEmailPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            {/* Header */}
            <div className="px-6 py-4 bg-custom-navy border-b border-custom-dark flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">
                Send Issue Email
              </h3>
              <button
                onClick={handleCloseEmailPopup}
                className="text-white hover:text-custom-light-100 transition-colors"
                disabled={isSendingEmail}
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              <div className="mb-4">
                <label
                  htmlFor="email-input"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Recipient Email Address
                </label>
                <input
                  id="email-input"
                  type="email"
                  value={emailAddress}
                  onChange={(e) => {
                    setEmailAddress(e.target.value);
                    setEmailValidationError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !isSendingEmail) {
                      handleConfirmSendEmail();
                    }
                  }}
                  placeholder="Enter email address"
                  disabled={isSendingEmail}
                  className={`w-full px-4 py-2.5 border rounded-lg shadow-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors ${
                    emailValidationError
                      ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                      : "border-gray-300"
                  } disabled:bg-gray-100 disabled:cursor-not-allowed`}
                  autoFocus
                />
                {emailValidationError && (
                  <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    {emailValidationError}
                  </p>
                )}
                {emailError && (
                  <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    {emailError}
                  </p>
                )}
              </div>

              {/* Issue Preview */}
              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <p className="text-xs text-gray-500 mb-1">Issue:</p>
                <p className="text-sm font-medium text-gray-900">
                  {localIssue.ruleName || "Issue"}
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  {localIssue.elementName}
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 bg-gray-50">
              <button
                onClick={handleCloseEmailPopup}
                disabled={isSendingEmail}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSendEmail}
                disabled={isSendingEmail || !emailAddress.trim()}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium shadow-sm transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSendingEmail ? (
                  <>
                    <svg
                      className="animate-spin h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Sending...
                  </>
                ) : (
                  <>
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                    Send Email
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
